import { memo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { LoginConstants } from '../../config/keyClock.config';
import { Spinner } from '@/components/ui/spinner';
import NoOrganizations from '@/components/ui/no-organizations';
import { Variants } from '@/types/common';
import { showAlert } from '../../slice/AlertSlice';
import {
  getAccessTokenFromApi,
  getCodeVerifierFromSessionStorage,
  processTokenData,
} from './callBack.service';
import { setInterceptor } from '../../services/axios-interceptor';
import { endSession, refreshTokenOnce } from '../../services/auth-token';
import { useSetUserContextMutation } from '../organization-selection/services/orgSelectionApi';
import { normalizeEntityClaim } from '../../services/entity-claim';
import { useNavigationService } from '../../services/navigate-service';
import { useScopeBootstrap } from '../../hooks/useScopeBootstrap';

const CallBack = memo(function CallBack() {
  const [searchParams] = useSearchParams();
  const { navigateTo } = useNavigationService();
  const dispatch = useDispatch();
  const [setUserContext] = useSetUserContextMutation();
  const { noOrganizations, resolveScopeAndLand } = useScopeBootstrap();

  useEffect(() => {
    const code: string | null = searchParams.get(LoginConstants.CODE);
    const codeVerifier: string | null = getCodeVerifierFromSessionStorage();

    if (code && codeVerifier) {
      fetchAccessToken(code, codeVerifier);
    } else {
      sessionStorage.clear();
      navigateTo('/');
    }
  }, []);

  const fetchAccessToken = (code: string, codeVerifier: string) => {
    getAccessTokenFromApi(code, codeVerifier)
      .then(async (response) => {
        const parseTokenData = processTokenData(response.data, true);
        localStorage.setItem('userEmail', parseTokenData.email);
        localStorage.setItem('userId', parseTokenData.sub);
        setInterceptor();

        // Replay the user's stored context into Keycloak so JWT claims
        // (EntityUUID, rootParentUUID, subEntities) are populated before any
        // downstream API calls that depend on them.
        //
        // DV-3573 review: a REFUSAL no longer ends the login. A switch stopped
        // writing these attributes, so they now stay wherever the user's last
        // switch before DV-3573 left them — and user-service authorises the
        // replay (DV-3146). If the user has since lost that account, or it was
        // disabled or deleted, failing here would fail every login with
        // nothing the user could do about it. The scope this tab works in
        // comes from the slice and `X-Entity-UUID`, which the backend checks
        // per request, so the token keeps its existing claims and the login
        // continues. Only the refresh after a SUCCESSFUL replay is needed.
        const currentContext = parseTokenData.CurrentContext;
        if (currentContext && parseTokenData.sub) {
          const replayed = await setUserContext({
            groupName: currentContext,
            userId: parseTokenData.sub,
            // DV-3027: CurrentContext replays the name the account had when the
            // user last switched into it. If it has been renamed since, the id
            // is what lets user-service find it instead of failing the login.
            entityId: normalizeEntityClaim(parseTokenData.EntityUUID),
          })
            .unwrap()
            .then(
              () => true,
              (error: unknown) => {
                console.warn('Login: stored context refused; keeping the token as issued.', error);
                return false;
              },
            );
          // Refresh through the shared lock so this call doesn't race the
          // request interceptor's own refresh on whatever fires next.
          if (replayed) await refreshTokenOnce();
        }

        // The registration check and the scope resolution are both the
        // shared step's now (DV-3012 §3.1): this screen's copy of the first
        // was the only one, which left the returning-session path at '/'
        // with no way back to the additional-details form.
        //
        // `getOrgDetails` and the DV-3175 recursion guard went with that
        // hand-off. The two functions used to call each other — an empty org
        // list sent us to the user check, and a user who already had an
        // entity sent us straight back — and nothing in between mutated
        // anything, so the pair recursed until the tab was closed (2,986
        // requests in 24 hours from one account, DV-2897). With a single
        // owner of the org list the cycle cannot form, so the cap is
        // unnecessary rather than merely unused. The dead-end screen it
        // protected is preserved, still rendered rather than navigated.
        void resolveScopeAndLand();
      })
      .catch((error: unknown) => {
        console.error('Error while sign in', error);
        dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
        // Send the user back to '/' so they can retry — leaving them on
        // /callback with a half-authenticated state would just show the
        // spinner forever.
        endSession();
      });
  };

  if (noOrganizations) {
    return <NoOrganizations />;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <Spinner size="lg" />
      <span className="mt-2.5 text-xl text-foreground">
        Please wait..
      </span>
    </div>
  );
});

export default CallBack;
